(function () {
  const CONTROLLER_KEY = '_testimonialsSliderController';

  class TestimonialsSliderController {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-testimonials-track]');
      this.prevButton = root.querySelector('[data-testimonials-prev]');
      this.nextButton = root.querySelector('[data-testimonials-next]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.track) return;

      this.bindEvents();
      this.queueUpdate();
    }

    bindEvents() {
      const { signal } = this;

      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true, signal });
      this.prevButton?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.scrollByDir(-1);
        },
        { signal }
      );
      this.nextButton?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.scrollByDir(1);
        },
        { signal }
      );
      window.addEventListener('resize', () => this.queueUpdate(), { signal });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
        this.resizeObserver.observe(this.track);
        Array.from(this.track.children).forEach((child) => this.resizeObserver.observe(child));
      }
    }

    destroy() {
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      window.clearTimeout(this.scrollSyncTimer);
      window.cancelAnimationFrame(this.rafId);
    }

    queueUpdate() {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = window.requestAnimationFrame(() => this.updateButtons());
      });
    }

    getStep() {
      const item = this.track.querySelector('.testimonials__item');
      if (!item) return Math.max(this.track.clientWidth * 0.85, 1);

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    getScrollLeft() {
      // Normalize RTL browsers where scrollLeft can be negative or inverted.
      const left = this.track.scrollLeft;
      if (left < 0) return Math.abs(left);

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (left > maxScroll + 1) return Math.max(0, maxScroll - (left - maxScroll));

      return left;
    }

    isFirstSlideAtStart() {
      const first = this.track.querySelector('.testimonials__item');
      if (!first) return true;

      const trackRect = this.track.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      return firstRect.left >= trackRect.left - 2;
    }

    isLastSlideAtEnd() {
      const items = this.track.querySelectorAll('.testimonials__item');
      const last = items[items.length - 1];
      if (!last) return true;

      const trackRect = this.track.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      return lastRect.right <= trackRect.right + 2;
    }

    getScrollMetrics() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const scrollLeft = this.getScrollLeft();
      const threshold = 3;
      const canScroll = maxScroll > threshold;

      // Use scroll position and slide visibility so start/end is reliable across browsers.
      const atStart = !canScroll || scrollLeft <= threshold || this.isFirstSlideAtStart();
      const atEnd = !canScroll || scrollLeft >= maxScroll - threshold || this.isLastSlideAtEnd();

      return { maxScroll, scrollLeft, canScroll, atStart, atEnd };
    }

    setButtonState(button, disabled) {
      if (!button) return;

      button.disabled = Boolean(disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.classList.toggle('is-disabled', Boolean(disabled));

      if (disabled) {
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
    }

    scrollByDir(direction) {
      const { atStart, atEnd, canScroll } = this.getScrollMetrics();
      if (!canScroll) return;
      if (direction < 0 && atStart) return;
      if (direction > 0 && atEnd) return;

      this.track.scrollBy({ left: direction * this.getStep(), behavior: 'smooth' });
      window.clearTimeout(this.scrollSyncTimer);
      this.scrollSyncTimer = window.setTimeout(() => this.updateButtons(), 400);
    }

    updateButtons() {
      const { atStart, atEnd } = this.getScrollMetrics();
      this.setButtonState(this.prevButton, atStart);
      this.setButtonState(this.nextButton, atEnd);
    }
  }

  function initTestimonialsSliders(scope) {
    const roots = (scope || document).querySelectorAll('[data-testimonials-slider]');
    roots.forEach((root) => {
      if (root[CONTROLLER_KEY]) {
        root[CONTROLLER_KEY].destroy();
        root[CONTROLLER_KEY] = null;
      }
      root[CONTROLLER_KEY] = new TestimonialsSliderController(root);
    });
  }

  window.initTestimonialsSliders = initTestimonialsSliders;

  document.addEventListener('DOMContentLoaded', () => initTestimonialsSliders(document));
  document.addEventListener('shopify:section:load', (event) => initTestimonialsSliders(event.target));
  document.addEventListener('shopify:section:reorder', () => initTestimonialsSliders(document));

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initTestimonialsSliders(document);
  }
})();
